import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import { toast } from "sonner";
import ModelPicker from "./components/ModelPicker";
import MediaUploader from "./components/MediaUploader";
import MediaGallery from "./components/MediaGallery";
import { Plus, Trash2, Wallet, Building2, CalendarClock, Sparkles, Send, Clock, PenLine, ShieldCheck, GraduationCap } from "lucide-react";

const fmt = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

// ==================== ESTATE VALUE DASHBOARD ====================
export function EstateValuePage() {
  const [d, setD] = useState(null);
  useEffect(() => { A.estateValue().then(r => setD(r.data)); }, []);
  if (!d) return <div>Loading…</div>;
  return (
    <div data-testid="estate-value-page">
      <H1>Estate Value Dashboard</H1>
      <Sub>An estimated view of your estate from the assets you've recorded. All figures are user-entered estimates, unverified, and may omit assets — verify with professionals.</Sub>
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <Card data-testid="ev-gross"><div className="label-caps">Gross estate (est.)</div><div className="text-3xl font-display" style={{ color: 'var(--primary)' }}>{fmt(d.gross_estate_estimate)}</div></Card>
        <Card data-testid="ev-liabilities"><div className="label-caps">Liabilities (est.)</div><div className="text-3xl font-display" style={{ color: 'var(--overdue)' }}>{fmt(d.liabilities_estimate)}</div></Card>
        <Card data-testid="ev-net"><div className="label-caps">Net estate (est.)</div><div className="text-3xl font-display" style={{ color: 'var(--primary)' }}>{fmt(d.net_estate_estimate)}</div></Card>
      </div>
      <H2>By asset class</H2>
      <Card>
        {Object.keys(d.by_asset_class).length === 0 ? <div className="text-sm" style={{ color: 'var(--muted)' }}>No physical assets recorded yet.</div> :
          Object.entries(d.by_asset_class).map(([k, v]) => (
            <div key={k} className="flex justify-between py-1 border-b text-sm" style={{ borderColor: "#eee" }}><span>{k}</span><span className="font-medium">{fmt(v)}</span></div>
          ))}
        <div className="text-xs mt-3" style={{ color: 'var(--muted)' }}>{d.note} · as of {d.as_of}</div>
      </Card>
    </div>
  );
}

// ==================== ENTITY MANAGEMENT HUB ====================
export function EntityHubPage() {
  const [rows, setRows] = useState(null);
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    const [r, t] = await Promise.all([A.entityHub.list(), A.entityHub.types()]);
    setRows(r.data); setTypes(t.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const blank = () => ({ legal_name: "", entity_type: "LLC", formation_jurisdiction: "", registered_agent: "", ownership: "", tax_classification: "", next_report_due: "", status: "Active", notes: "" });
  const save = async () => {
    try { if (editing.id) await A.entityHub.patch(editing.id, editing); else await A.entityHub.create(editing);
      toast.success("Saved"); setEditing(null); load(); } catch (e) { toast.error("Save failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete entity?")) { await A.entityHub.del(id); load(); } };
  if (!rows) return <div>Loading…</div>;
  return (
    <div data-testid="entity-hub-page">
      <H1>Entity Management Hub</H1>
      <Sub>Track each entity's legal name, type, formation jurisdiction, ownership, tax classification, and filing deadlines. Legal form and tax classification are kept distinct. Overdue filings are flagged.</Sub>
      <div className="flex justify-end mb-3"><button className="btn-gold flex items-center gap-1" onClick={() => setEditing(blank())} data-testid="entity-new"><Plus size={14} /> Add entity</button></div>
      {editing && (
        <Card className="mb-4" data-testid="entity-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="label-caps">Legal name</label><input value={editing.legal_name} onChange={e => setEditing({ ...editing, legal_name: e.target.value })} data-testid="entity-name" /></div>
            <div><label className="label-caps">Type</label><select value={editing.entity_type} onChange={e => setEditing({ ...editing, entity_type: e.target.value })} data-testid="entity-type">{types.map(t => <option key={t}>{t}</option>)}</select></div>
            <div><label className="label-caps">Formation jurisdiction</label><input value={editing.formation_jurisdiction} onChange={e => setEditing({ ...editing, formation_jurisdiction: e.target.value })} /></div>
            <div><label className="label-caps">Tax classification</label><input value={editing.tax_classification} onChange={e => setEditing({ ...editing, tax_classification: e.target.value })} placeholder="e.g. Disregarded / S / C / Partnership" /></div>
            <div><label className="label-caps">Ownership</label><input value={editing.ownership} onChange={e => setEditing({ ...editing, ownership: e.target.value })} placeholder="e.g. 100% Holding LLC" /></div>
            <div><label className="label-caps">Registered agent</label><input value={editing.registered_agent} onChange={e => setEditing({ ...editing, registered_agent: e.target.value })} /></div>
            <div><label className="label-caps">Next report due</label><input type="date" value={editing.next_report_due} onChange={e => setEditing({ ...editing, next_report_due: e.target.value })} data-testid="entity-due" /></div>
            <div><label className="label-caps">Status</label><input value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })} /></div>
          </div>
          <div className="flex gap-2 mt-3"><button className="btn-primary" onClick={save} data-testid="entity-save">Save</button><button className="btn-outline" onClick={() => setEditing(null)}>Cancel</button></div>
        </Card>
      )}
      <div className="space-y-2">
        {rows.map(r => (
          <Card key={r.id} data-testid={`entity-${r.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div>
                <div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}><Building2 size={15} /> {r.legal_name}
                  <span className="chip text-xs">{r.entity_type}</span>
                  {r.overdue && <span className="chip text-xs" style={{ background: "#fdecea", color: "#a3271d" }}>Filing overdue</span>}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{[r.formation_jurisdiction, r.tax_classification && `tax: ${r.tax_classification}`, r.ownership, r.next_report_due && `due ${r.next_report_due}`].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="flex gap-1"><button className="btn-outline text-xs" onClick={() => setEditing(r)}>Edit</button><button className="btn-outline text-xs" style={{ color: 'var(--overdue)' }} onClick={() => del(r.id)}><Trash2 size={12} /></button></div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No entities yet.</div></Card>}
      </div>
    </div>
  );
}

// ==================== COMPLIANCE CALENDAR ====================
const BAND_COLOR = { "Overdue": { bg: "#fdecea", fg: "#a3271d" }, "7 days": { bg: "#fff0e6", fg: "#b3400f" }, "30 days": { bg: "#fff5e5", fg: "#8a5a00" }, "90 days": { bg: "#eef7ee", fg: "#2f6b34" }, "Later": { bg: "#eef", fg: "#3a3a7a" } };
export function ComplianceCalendarPage() {
  const [d, setD] = useState(null);
  useEffect(() => { A.complianceCalendar().then(r => setD(r.data)); }, []);
  if (!d) return <div>Loading…</div>;
  return (
    <div data-testid="compliance-calendar-page">
      <H1>Compliance Calendar</H1>
      <Sub>Aggregated deadlines — reminders and entity filings — color-coded by urgency at 90, 30, and 7 days. Scoped to your records.</Sub>
      <div className="grid md:grid-cols-4 gap-3 mb-5">
        {["Overdue", "7 days", "30 days", "90 days"].map(b => (
          <Card key={b} data-testid={`cc-count-${b.replace(' ', '')}`}><div className="label-caps">{b}</div><div className="text-2xl font-display" style={{ color: BAND_COLOR[b].fg }}>{d.counts[b] || 0}</div></Card>
        ))}
      </div>
      <div className="space-y-2">
        {d.items.map((i, idx) => (
          <Card key={idx} data-testid={`cc-item-${idx}`}>
            <div className="flex justify-between items-center gap-3">
              <div><div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}><CalendarClock size={15} /> {i.title}</div><div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{i.category} · due {i.due}</div></div>
              <span className="chip text-xs" style={{ background: BAND_COLOR[i.band]?.bg, color: BAND_COLOR[i.band]?.fg }}>{i.band}</span>
            </div>
          </Card>
        ))}
        {d.items.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No upcoming deadlines. Add reminders or entity filing dates.</div></Card>}
      </div>
    </div>
  );
}

// ==================== TIME CAPSULE ====================
export function TimeCapsulePage() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [mediaBy, setMediaBy] = useState({});
  const load = useCallback(async () => {
    const r = await A.timeCapsules.list(); setData(r.data);
    try {
      const m = await A.media.list("time_capsule");
      const grouped = {}; m.data.forEach(a => { (grouped[a.context_id] = grouped[a.context_id] || []).push(a); });
      setMediaBy(grouped);
    } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);
  const blank = () => ({ title: "", message: "", media_url: "", deliver_on: "", recipient_email: "", recipient_name: "" });
  const save = async () => {
    try { if (editing.id) await A.timeCapsules.patch(editing.id, editing); else await A.timeCapsules.create(editing);
      toast.success("Saved"); setEditing(null); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (window.confirm("Revoke this capsule?")) { try { await A.timeCapsules.del(id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } } };
  const run = async () => { const r = await A.timeCapsules.run(); toast.success(`Delivered ${r.data.delivered} due capsule(s)`); load(); };
  if (!data) return <div>Loading…</div>;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div data-testid="time-capsule-page">
      <H1>Time Capsule</H1>
      <Sub>Schedule a message to be delivered on a future date. Content stays invisible to the recipient until it unlocks; you may edit or revoke it any time before delivery.</Sub>
      <div className="flex justify-end gap-2 mb-3">
        <button className="btn-outline flex items-center gap-1" onClick={run} data-testid="capsule-run"><Send size={14} /> Deliver due now</button>
        <button className="btn-gold flex items-center gap-1" onClick={() => setEditing(blank())} data-testid="capsule-new"><Plus size={14} /> New capsule</button>
      </div>
      {editing && (
        <Card className="mb-4" data-testid="capsule-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="label-caps">Title</label><input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="capsule-title" /></div>
            <div><label className="label-caps">Deliver on</label><input type="date" value={editing.deliver_on} onChange={e => setEditing({ ...editing, deliver_on: e.target.value })} data-testid="capsule-date" /></div>
            <div><label className="label-caps">Recipient email</label><input value={editing.recipient_email} onChange={e => setEditing({ ...editing, recipient_email: e.target.value })} data-testid="capsule-recipient" /></div>
            <div><label className="label-caps">Recipient name</label><input value={editing.recipient_name} onChange={e => setEditing({ ...editing, recipient_name: e.target.value })} /></div>
            <div className="md:col-span-2"><label className="label-caps">Message</label><textarea rows={3} value={editing.message} onChange={e => setEditing({ ...editing, message: e.target.value })} data-testid="capsule-message" /></div>
            <div className="md:col-span-2"><label className="label-caps">Media link (optional)</label><input value={editing.media_url} onChange={e => setEditing({ ...editing, media_url: e.target.value })} /></div>
            {editing.id && (
              <div className="md:col-span-2">
                <label className="label-caps">Attach photos, video or audio</label>
                <div className="mt-1"><MediaUploader contextType="time_capsule" contextId={editing.id} visibility="Shared" label="Add media" onUploaded={load} testid="capsule-media-uploader" /></div>
                {(mediaBy[editing.id] || []).length > 0 && <div className="mt-2"><MediaGallery assets={mediaBy[editing.id]} onChanged={load} compact /></div>}
              </div>
            )}
            {!editing.id && <div className="md:col-span-2 text-xs" style={{ color: 'var(--muted)' }}>Save the capsule first, then reopen it to attach photos, video, or audio.</div>}
          </div>
          <div className="flex gap-2 mt-3"><button className="btn-primary" onClick={save} data-testid="capsule-save">Save</button><button className="btn-outline" onClick={() => setEditing(null)}>Cancel</button></div>
        </Card>
      )}
      <H2>Created by you</H2>
      <div className="space-y-2 mb-6">
        {data.created.map(c => (
          <Card key={c.id} data-testid={`capsule-${c.id}`}>
            <div className="flex justify-between items-center gap-3">
              <div><div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}><Clock size={15} /> {c.title}
                {c.delivered ? <span className="chip chip-green text-xs">Delivered</span> : <span className="chip chip-amber text-xs">Scheduled {c.deliver_on}</span>}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>For {c.recipient_name || c.recipient_email}</div></div>
              {!c.delivered && <div className="flex gap-1"><button className="btn-outline text-xs" onClick={() => setEditing(c)}>Edit</button><button className="btn-outline text-xs" style={{ color: 'var(--overdue)' }} onClick={() => del(c.id)}><Trash2 size={12} /></button></div>}
            </div>
            {(mediaBy[c.id] || []).length > 0 && <div className="mt-2"><MediaGallery assets={mediaBy[c.id]} canManage={false} compact /></div>}
          </Card>
        ))}
        {data.created.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No capsules created yet.</div></Card>}
      </div>
      {data.received.length > 0 && <>
        <H2>Delivered to you</H2>
        <div className="space-y-2">
          {data.received.map(c => (
            <Card key={c.id} data-testid={`capsule-recv-${c.id}`}>
              <div className="font-medium" style={{ color: 'var(--primary)' }}>{c.title}</div>
              <div className="text-sm mt-1">{c.message}</div>
              {c.media_url && <a className="text-xs" href={c.media_url} target="_blank" rel="noreferrer">View media</a>}
              {(mediaBy[c.id] || []).length > 0 && <div className="mt-2"><MediaGallery assets={mediaBy[c.id]} canManage={false} compact /></div>}
            </Card>
          ))}
        </div>
      </>}
    </div>
  );
}

// ==================== AI ESTATE PLANNING ASSISTANT ====================
export function EstateAssistantPage() {
  const [q, setQ] = useState("");
  const [model, setModel] = useState("claude");
  const [ans, setAns] = useState(null);
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true); setAns(null);
    try { const r = await A.estateAssistant(q, model); setAns(r.data); } catch (e) { toast.error("Assistant unavailable"); }
    setBusy(false);
  };
  return (
    <div data-testid="estate-assistant-page">
      <H1>AI Estate Assistant</H1>
      <Sub>Ask about your own estate records. The assistant answers only from records you're authorized to see, cites the record types it used, and never invents facts. It provides information, not legal advice.</Sub>
      <Card className="mb-4">
        <label className="label-caps">Your question</label>
        <textarea rows={2} value={q} onChange={e => setQ(e.target.value)} placeholder="e.g. Which of my assets don't have a beneficiary noted?" data-testid="assistant-input" />
        <div className="flex flex-wrap items-end gap-3 mt-2">
          <button className="btn-primary flex items-center gap-1" disabled={busy} onClick={ask} data-testid="assistant-ask"><Sparkles size={14} /> {busy ? "Thinking…" : "Ask"}</button>
          <div>
            <div className="label-caps mb-1">AI Model</div>
            <ModelPicker value={model} onChange={setModel} testid="assistant-model-picker" />
          </div>
        </div>
      </Card>
      {ans && (
        <Card data-testid="assistant-answer">
          <div className="text-sm whitespace-pre-wrap">{ans.answer}</div>
          <div className="text-xs mt-3" style={{ color: 'var(--muted)' }}>Considered {ans.records_considered} authorized record(s). Informational only — professional review required.</div>
        </Card>
      )}
    </div>
  );
}

// ==================== E-SIGNATURE REQUESTS ====================
export function SignatureRequestsPage() {
  const [rows, setRows] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    const [r, s, d] = await Promise.all([A.signatures.list(), A.signatures.statuses(), A.draftsList()]);
    setRows(r.data); setStatuses(s.data); setDrafts(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const blank = () => ({ draft_id: "", title: "", signers: [{ name: "", email: "", capacity: "", order: 1 }], witness_required: false, notary_required: false, version_note: "" });
  const save = async () => {
    try { await A.signatures.create(editing); toast.success("Signature request created"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (id, status) => { await A.signatures.setStatus(id, status); load(); };
  if (!rows) return <div>Loading…</div>;
  return (
    <div data-testid="signatures-page">
      <H1>Signature Requests</H1>
      <Sub>Provider-neutral e-signature workflow. Confirm the version, signers, capacities, and witness/notary requirements, then track status. No external provider is connected until Trustee Smith approves one; unsigned drafts and versions are preserved.</Sub>
      <div className="flex justify-end mb-3"><button className="btn-gold flex items-center gap-1" onClick={() => setEditing(blank())} data-testid="sig-new"><Plus size={14} /> New request</button></div>
      {editing && (
        <Card className="mb-4" data-testid="sig-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="label-caps">Title</label><input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="sig-title" /></div>
            <div><label className="label-caps">Draft</label><select value={editing.draft_id} onChange={e => setEditing({ ...editing, draft_id: e.target.value })} data-testid="sig-draft"><option value="">Select a draft…</option>{drafts.map(d => <option key={d.id} value={d.id}>{d.Title}</option>)}</select></div>
          </div>
          <div className="mt-3">
            <label className="label-caps">Signers</label>
            {editing.signers.map((s, i) => (
              <div key={i} className="grid md:grid-cols-4 gap-2 mb-2">
                <input placeholder="Name" value={s.name} onChange={e => { const n = [...editing.signers]; n[i] = { ...s, name: e.target.value }; setEditing({ ...editing, signers: n }); }} />
                <input placeholder="Email" value={s.email} onChange={e => { const n = [...editing.signers]; n[i] = { ...s, email: e.target.value }; setEditing({ ...editing, signers: n }); }} />
                <input placeholder="Capacity" value={s.capacity} onChange={e => { const n = [...editing.signers]; n[i] = { ...s, capacity: e.target.value }; setEditing({ ...editing, signers: n }); }} />
                <input type="number" placeholder="Order" value={s.order} onChange={e => { const n = [...editing.signers]; n[i] = { ...s, order: Number(e.target.value) }; setEditing({ ...editing, signers: n }); }} />
              </div>
            ))}
            <button className="btn-outline text-xs" onClick={() => setEditing({ ...editing, signers: [...editing.signers, { name: "", email: "", capacity: "", order: editing.signers.length + 1 }] })}>+ Add signer</button>
          </div>
          <div className="flex gap-4 mt-3 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" className="w-auto" checked={editing.witness_required} onChange={e => setEditing({ ...editing, witness_required: e.target.checked })} /> Witness required</label>
            <label className="flex items-center gap-2"><input type="checkbox" className="w-auto" checked={editing.notary_required} onChange={e => setEditing({ ...editing, notary_required: e.target.checked })} /> Notary required</label>
          </div>
          <div className="flex gap-2 mt-3"><button className="btn-primary" onClick={save} data-testid="sig-save">Create request</button><button className="btn-outline" onClick={() => setEditing(null)}>Cancel</button></div>
        </Card>
      )}
      <div className="space-y-2">
        {rows.map(r => (
          <Card key={r.id} data-testid={`sig-${r.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div>
                <div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}><PenLine size={15} /> {r.title} <span className="chip text-xs">{r.status}</span></div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{(r.signers || []).map(s => `${s.name || s.email} (${s.capacity || "signer"})`).join(", ")}{r.notary_required ? " · notary" : ""}{r.witness_required ? " · witness" : ""}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{r.provider}</div>
              </div>
              <select value={r.status} onChange={e => setStatus(r.id, e.target.value)} data-testid={`sig-status-${r.id}`}>{statuses.map(s => <option key={s}>{s}</option>)}</select>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No signature requests yet.</div></Card>}
      </div>
    </div>
  );
}

// ==================== NEXT-GENERATION TRAINING ====================
const NEXTGEN_PATHWAY = [
  { key: "NEXTGEN-STEWARD", step: 1, why: "Begin with mindset: stewardship over entitlement, and how family capital works." },
  { key: "WEALTH-VALUES", step: 2, why: "Connect wealth to values, communication, and purposeful giving." },
  { key: "HEIR-FINANCE", step: 3, why: "Build practical money skills: budgeting, credit, investing, and advisers." },
  { key: "ESTATE-FUND", step: 4, why: "Understand the estate documents your family relies on." },
  { key: "TRUST-ADMIN", step: 5, why: "Learn how trusts are administered and what fiduciaries do." },
  { key: "LEGAL-LIT", step: 6, why: "Read legal documents with confidence before taking on responsibility." },
];

export function NextGenTrainingPage() {
  const nav = useNavigate();
  const [courses, setCourses] = useState(null);
  useEffect(() => { A.courses().then(r => setCourses(r.data)); }, []);
  if (!courses) return <div>Loading…</div>;
  const byKey = Object.fromEntries(courses.map(c => [c.key, c]));
  return (
    <div data-testid="nextgen-training-page">
      <H1>Next-Generation Training</H1>
      <Sub>A guided learning pathway that prepares rising family members to steward the family's legacy responsibly — from mindset and values to financial literacy, estate awareness, and reading legal documents. Complete the tracks in order; progress and certificates are tracked in the Academy.</Sub>
      <div className="space-y-3">
        {NEXTGEN_PATHWAY.map(p => {
          const c = byKey[p.key];
          const done = c && c.pct === 100;
          return (
            <Card key={p.key} data-testid={`nextgen-${p.key}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-display" style={{ background: done ? "#2f6b34" : "#C9A050", color: "#1B3B2B" }}>{p.step}</div>
                  <div>
                    <div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}><GraduationCap size={15} /> {c ? c.title : p.key}
                      {c && typeof c.pct === "number" && <span className="chip text-xs">{c.pct}%</span>}</div>
                    <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{p.why}</div>
                  </div>
                </div>
                {c && <button className="btn-outline text-xs" onClick={() => nav(`/academy/${c.id}`)} data-testid={`nextgen-open-${p.key}`}>Open track</button>}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
